import { KpkFolder, removeUndefinedDeep } from "@keepeek/commons";
import { useMosaicWidgets } from "@keepeek/refront-components";
import { useRecoilState } from "recoil";

import { LinkType } from "../../models/configuration/components/mosaic";
import { Template } from "../../models/configuration/definitions/template";
import { Type } from "../../models/configuration/definitions/widget";
import { KMosaicWidgetProps } from "./KMosaicWidget";
import { WidgetConfigurations, WidgetTypeExtended } from "./models";
import { widgetStateSelector } from "./selectors";

export const useWidgetState = ({ id, type }: { id: string; type: WidgetTypeExtended }) => {
  const [widgetState, setWidgetState] = useRecoilState(widgetStateSelector({ id, type }));

  return { widgetState, setWidgetState };
};

type UsePreloadWidgets = {
  loading: {
    loadingMosaics: boolean;
  };
  templatesConfiguration: Template[];
  widgetsConfiguration: WidgetConfigurations | null;
};

export const usePreloadWidgets = ({
  templatesConfiguration,
  widgetsConfiguration,
}: {
  templatesConfiguration: Template[];
  widgetsConfiguration: WidgetConfigurations | null;
}): UsePreloadWidgets => {
  const widgetsInTemplate = removeUndefinedDeep(
    templatesConfiguration.flatMap((tc) => tc.columns?.flatMap((c) => c.widgets)),
  );
  // Preload Mosaics
  // Extract folderIds
  const widgetMosaicsIds: KMosaicWidgetProps["widgetId"][] = removeUndefinedDeep(
    widgetsInTemplate.map((w) => (w.type === Type.Mosaic ? w.id : undefined)),
  );
  const widgetMosaics: KMosaicWidgetProps[] = removeUndefinedDeep(
    widgetMosaicsIds.map((id) => widgetsConfiguration?.[`${Type.Mosaic} - ${id}`]),
  ) as KMosaicWidgetProps[];

  const folderIds: KpkFolder["id"][] = widgetMosaics.flatMap((wm) => {
    // Add folder from classic mosaic :
    const ret: KpkFolder["id"][] = wm.folderIds;
    // Add folder from hybrid mosaic :
    wm.mosaicElements?.forEach((element) => {
      if (element.linkType === LinkType.Folder && element.folderId) {
        ret.push(element.folderId);
      }
    });
    return ret;
  });

  // Load mosaic with countMedias to hide empty folders
  // Use it for preload
  const { loading: loadingMosaics } = useMosaicWidgets({ folderIds, countMedias: true });
  // During preload, remove mosaic from templates
  const templatesConfigurationWithoutMosaic = templatesConfiguration.map((tc) => ({
    ...tc,
    columns: tc.columns?.map((c) => ({
      ...c,
      widgets: c.widgets?.filter((w) => w.type !== Type.Mosaic),
    })),
  }));
  const newTemplatesConfiguration = loadingMosaics
    ? templatesConfigurationWithoutMosaic
    : templatesConfiguration;

  return {
    loading: {
      loadingMosaics,
    },
    widgetsConfiguration,
    templatesConfiguration: newTemplatesConfiguration,
  };
};
